// apps/client/src/ui-kit/Feedback.tsx
//
// Modal and WinModal components — V2 Beelucky Sweepstakes.
// WinModal: Lottie confetti animation, animated BingoChips, "Prepare Next Game" CTA (post-game loop).
//

import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from 'lottie-react';
import { Button } from './Button';
import { imgAdPrompt } from '../assets';
import Icon from './Icon';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';

import { useTranslation } from 'react-i18next';
import { useAudio } from '../providers/AudioProvider';
import confettiAnimation from '../assets/animations/confetti.json';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    variant?: 'default' | 'gold';
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    children,
    title,
    variant = 'default',
}) => {
    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />

                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={
                            variant === 'gold'
                                ? {
                                    scale: 1, opacity: 1, y: 0,
                                    rotate: [0, -3, 3, -3, 3, 0], // Screen shake
                                    transition: { duration: 0.5 }
                                }
                                : { scale: 1, opacity: 1, y: 0 }
                        }
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        className={`
                            relative w-full max-w-sm rounded-3xl p-6 overflow-hidden
                            ${variant === 'gold'
                                ? 'bg-white shadow-[0_0_50px_rgba(251,191,36,0.3)] border border-primary/20'
                                : 'bg-white shadow-card-white border border-gray-100'
                            }
                        `}
                    >
                        {title && (
                            <h2 className="text-2xl font-black text-center text-[#1A0B2E] uppercase mb-6 font-display">
                                {title}
                            </h2>
                        )}
                        {children}

                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-[#1A0B2E]/30 hover:text-[#1A0B2E]/60"
                        >
                            <Icon name="close" />
                        </button>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export const WinModal: React.FC<{
    amount: number;
    title?: string;
    currency?: string;
    isOpen: boolean;
    onClose: () => void;
    onClaim: () => void;
    /** If true, CTA says "Prepare Next Game" and loops to lobby */
    onPrepareNext?: () => void;
}> = ({ amount, title, currency = 'BingoChips', isOpen, onClose, onClaim, onPrepareNext }) => {
    const animatedAmount = useAnimatedCounter(isOpen ? amount : 0, { duration: 1500 });
    const { playBingoWin, playWinSmallPrize } = useAudio();
    const { t } = useTranslation();

    useEffect(() => {
        if (isOpen) {
            if (title && title.toUpperCase().includes('BINGO')) {
                playBingoWin();
            } else {
                playWinSmallPrize();
            }
        }
    }, [isOpen, title, playBingoWin, playWinSmallPrize]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title || t('game.winTitle')} variant="gold">
            {/* Lottie confetti overlay */}
            {isOpen && (
                <div className="absolute inset-0 pointer-events-none z-0 overflow-hidden rounded-3xl">
                    <Lottie
                        animationData={confettiAnimation}
                        loop={false}
                        autoplay={true}
                        style={{ width: '100%', height: '100%' }}
                    />
                </div>
            )}

            <div className="text-center relative z-10">
                <motion.div
                    className="text-6xl mb-4"
                    animate={{ scale: [1, 1.2, 1], rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 1, repeat: 2 }}
                >
                    🏆
                </motion.div>
                <p className="text-[#1A0B2E]/50 text-sm font-bold uppercase mb-2">{t('game.youWon')}</p>
                <div className="text-4xl font-black gradient-text-gold mb-2">
                    +{animatedAmount.toLocaleString()}
                </div>
                <p className="text-[#1A0B2E]/40 text-sm mb-6">🌟 {currency}</p>

                {/* Primary CTA — post-game loop */}
                <Button
                    onClick={() => {
                        onClaim();
                        onPrepareNext?.();
                    }}
                    size="lg"
                    fullWidth
                >
                    {onPrepareNext ? t('game.prepareNext') : t('game.claimReward')}
                </Button>
            </div>
        </Modal>
    );
};

export const AdPrompt: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onWatch: () => void;
    reward: number;
}> = ({ isOpen, onClose, onWatch, reward }) => {
    const { t } = useTranslation();
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('store.freeChips')}>
            <div className="text-center">
                <img src={imgAdPrompt} alt="Watch Ad" className="w-24 h-24 mx-auto mb-4" />
                <p className="text-[#1A0B2E] text-lg font-bold mb-6">
                    {t('store.watchAdEarn')} <span className="gradient-text-gold font-black">{reward} BingoChips</span> {t('store.instantly')}
                </p>

                <div className="flex flex-col gap-3">
                    <Button onClick={onWatch} icon="play_circle" fullWidth>
                        {t('store.watchAndEarn', { reward })}
                    </Button>
                    <Button variant="ghost" onClick={onClose} size="sm">
                        {t('store.notNow')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export const GlobalWinToast: React.FC<{
    message: string;
    prize: number;
    isVisible: boolean;
    currency?: string;
}> = ({ message, prize, isVisible, currency = 'Chips' }) => {
    const { t } = useTranslation();
    const { playPopLoss } = useAudio();

    useEffect(() => {
        if (isVisible) {
            playPopLoss();
        }
    }, [isVisible, playPopLoss]);

    return (
        <AnimatePresence>
            {isVisible && (
                <div className="fixed inset-0 z-[400] flex items-center justify-center p-4">
                    {/* Dark backdrop overlay to grab attention without clicking */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    />

                    {/* Modal Content */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -20 }}
                        className="relative bg-[#1A0B2E] border-2 border-[#FBBF24]/50 px-8 py-6 rounded-3xl shadow-[0_0_50px_rgba(251,191,36,0.3)] flex flex-col items-center justify-center min-w-[280px] max-w-sm pointer-events-none"
                    >
                        <motion.div
                            className="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-[#FBBF24] to-[#F59E0B] flex items-center justify-center text-white text-3xl shadow-inner shadow-white/30"
                            animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            🏆
                        </motion.div>
                        <div className="text-white font-black flex-1 text-xl text-center leading-tight uppercase tracking-wider mb-3">
                            {message}
                        </div>
                        <div className="text-white/80 text-sm font-bold bg-white/10 px-5 py-2 rounded-full">
                            {t('game.prize')} <span className="text-[#FBBF24] font-black ml-1">{prize.toLocaleString()} {currency}</span>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

